import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, FolderTree, FileType2, Files,
  Users, Shield, Clock, Settings, Folder, ChevronRight,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useUser } from '@/contexts/UserContext'
import { Permissions } from '@/lib/permissions'
import { listUsers, userKeys } from '@/lib/api/users'

const NAV = [
  { id: 'dash',     label: 'Visão geral',         icon: LayoutDashboard, to: '/admin' },
  { type: 'section', label: 'Conteúdo' },
  { id: 'tree',     label: 'Árvore de áreas',     icon: FolderTree,      to: '/admin/areas' },
  { id: 'types',    label: 'Tipos de arquivo',    icon: FileType2,       to: '/admin/types' },
  { id: 'files',    label: 'Arquivos',            icon: Files,           to: '/admin/files' },
  { type: 'section', label: 'Pessoas' },
  { id: 'users',    label: 'Usuários',            icon: Users,           to: '/admin/users', badge: '—' },
  { id: 'profiles', label: 'Perfis e permissões', icon: Shield,          to: '/admin/profiles' },
  { type: 'section', label: 'Plataforma' },
  { id: 'audit',    label: 'Auditoria',           icon: Clock,           to: '/admin/audit' },
  { id: 'settings', label: 'Configurações',       icon: Settings,        to: '/admin/settings' },
] as const

export function AdminSidebar() {
  const { pathname } = useLocation()
  const { user, hasPermission } = useUser()
  const canViewUsers = hasPermission(Permissions.OtherUsersView)
  const canViewProfiles = hasPermission(Permissions.ProfileManagementView)
  const canViewDocumentTypes = hasPermission(Permissions.DocumentTypesView)
  const canViewAreas = hasPermission(Permissions.AreaTreeView)
  const canViewDocuments = hasPermission(Permissions.DocumentManagementView)
  const canViewSettings = hasPermission(Permissions.SettingsView)
  const canViewAudit = hasPermission(Permissions.AuditLogsView)

  const { data: userCountData } = useQuery({
    queryKey: userKeys.list({ pageSize: 1, page: 1 }),
    queryFn: () => listUsers({ pageSize: 1, page: 1 }),
    enabled: canViewUsers,
    staleTime: 60_000,
  })
  const userTotal = userCountData?.total

  const initials = user?.email
    ? user.email.slice(0, 2).toUpperCase()
    : 'AD'

  return (
    <aside style={{
      width: 240,
      background: '#1A1F24',
      color: 'rgba(255,255,255,0.86)',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      height: '100%',
      overflow: 'hidden',
    }}>
      <div style={{ padding: '16px 14px 8px', fontSize: 10, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em', fontWeight: 600 }}>
        PAINEL ADMINISTRATIVO
      </div>

      <nav style={{ padding: '0 8px', flex: 1, overflow: 'auto' }}>
        {NAV.map((item, i) => {
          if ('id' in item && item.id === 'users' && !canViewUsers) return null
          if ('id' in item && item.id === 'profiles' && !canViewProfiles) return null
          if ('id' in item && item.id === 'types' && !canViewDocumentTypes) return null
          if ('id' in item && item.id === 'tree' && !canViewAreas) return null
          if ('id' in item && item.id === 'files' && !canViewDocuments) return null
          if ('id' in item && item.id === 'settings' && !canViewSettings) return null
          if ('id' in item && item.id === 'audit' && !canViewAudit) return null
          const badgeValue = 'badge' in item && item.id === 'users' && userTotal != null
            ? userTotal.toLocaleString('pt-BR')
            : 'badge' in item ? item.badge : undefined
          if ('type' in item) return (
            <div key={i} style={{ padding: '14px 10px 4px', fontSize: 9, color: 'rgba(255,255,255,0.40)', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600 }}>
              {item.label}
            </div>
          )
          const active = pathname === item.to || (item.to !== '/admin' && pathname.startsWith(item.to))
          const Icon = item.icon
          return (
            <Link key={item.id} to={item.to} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '7px 10px', borderRadius: 5,
              background: active ? 'rgba(33,111,183,0.18)' : 'transparent',
              color: active ? '#FFA571' : 'rgba(255,255,255,0.86)',
              fontSize: 13, fontWeight: active ? 600 : 500,
              textDecoration: 'none', marginBottom: 1,
            }}>
              <Icon size={15} />
              <span style={{ flex: 1 }}>{item.label}</span>
              {badgeValue && (
                <span style={{
                  fontSize: 10, fontWeight: 600,
                  background: 'rgba(255,255,255,0.12)',
                  color: 'rgba(255,255,255,0.6)',
                  borderRadius: 9999,
                  padding: '1px 6px',
                }}>{badgeValue}</span>
              )}
            </Link>
          )
        })}
      </nav>

      {/* Navigation link */}
      <Link to="/" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        margin: '12px 12px 0',
        padding: '8px 10px',
        background: 'rgba(33,111,183,0.12)',
        color: '#FFA571', fontSize: 12, fontWeight: 600,
        textDecoration: 'none', borderRadius: 5,
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Folder size={13} /> Navegação e leitura
        </span>
        <ChevronRight size={11} />
      </Link>

      {/* User profile */}
      <div style={{
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: '#1F3A5F',
          color: 'white',
          display: 'grid', placeItems: 'center',
          fontWeight: 600, fontSize: 12, flexShrink: 0,
        }}>
          {initials}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.9)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {user?.email?.split('@')[0] ?? ''}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Admin</div>
        </div>
      </div>
    </aside>
  )
}
